import React, { useMemo, useState } from 'react';
import classNames from 'classnames';
import useComponentVisible from '../../helpers/hooks/useComponentVisible';
import MyCheckBox from '../MyCheckbox';
import './css/index.scss';

const SEARCH_THRESHOLD = 8;

/** Подпись + контрол + необязательная подсказка/ошибка. */
export function FilterField({
  label, hint, error, wide = false, children,
}) {
  return (
    <div className={classNames('filter_field', { filter_field_wide: wide })}>
      <span className="filter_field_label">{label}</span>
      {children}
      {error ? <span className="filter_field_error">{error}</span> : null}
      {!error && hint ? <span className="filter_field_hint">{hint}</span> : null}
    </div>
  );
}

/** Блок фильтров с заголовком («Где», «Что», «Служебные» …). */
export function FiltersGroup({ title, children }) {
  return (
    <div className="filters_group">
      <p className="filters_group_title">{title}</p>
      <div className="filters_group_fields">{children}</div>
    </div>
  );
}

/** Одиночный выбор: пустое значение = «не фильтруем». */
export function FilterSelect({
  label,
  value,
  options,
  onChange,
  placeholder = 'Любой',
  allowEmpty = true,
  disabled = false,
  hint,
  loading = false,
}) {
  return (
    <FilterField label={label} hint={hint}>
      <select
        className="filter_select"
        value={value || ''}
        disabled={disabled || loading}
        onChange={(e) => onChange(e.target.value)}
      >
        {allowEmpty || loading ? <option value="">{loading ? 'Загрузка…' : placeholder}</option> : null}
        {options.map((option) => (
          <option key={option.value} value={option.value}>{option.label}</option>
        ))}
      </select>
    </FilterField>
  );
}

/** Множественный выбор с поиском (если вариантов много) и кнопкой «Сбросить». */
export function FilterMultiSelect({
  label,
  options,
  values,
  onChange,
  placeholder = 'Все',
  emptyText = 'Нет данных',
  disabled = false,
  loading = false,
  hint,
}) {
  const { ref, isComponentVisible, setIsComponentVisible } = useComponentVisible(false);
  const [search, setSearch] = useState('');

  const summary = useMemo(() => {
    if (loading) return 'Загрузка…';
    if (!values.length) return placeholder;
    const names = values.map((id) => options.find((option) => option.value === id)?.label || '…');
    if (names.length <= 2) return names.join(', ');
    return `Выбрано: ${names.length}`;
  }, [loading, values, options, placeholder]);

  const visibleOptions = useMemo(() => {
    const needle = search.trim().toLowerCase();
    if (!needle) return options;
    return options.filter((option) => option.label.toLowerCase().includes(needle));
  }, [options, search]);

  const toggle = (value) => {
    onChange(values.includes(value) ? values.filter((id) => id !== value) : [...values, value]);
  };

  const onTriggerClick = () => {
    if (disabled || loading) return;
    setIsComponentVisible((prev) => !prev);
    setSearch('');
  };

  return (
    <FilterField label={label} hint={hint}>
      <div className="filter_multi" ref={ref}>
        <button
          type="button"
          className={classNames('filter_multi_trigger', {
            filter_multi_trigger_filled: values.length > 0,
            filter_multi_trigger_open: isComponentVisible,
          })}
          disabled={disabled || loading}
          onClick={onTriggerClick}
        >
          {summary}
        </button>

        {isComponentVisible ? (
          <div className="filter_multi_panel">
            {options.length > SEARCH_THRESHOLD ? (
              <input
                className="filter_multi_search"
                placeholder="Найти…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            ) : null}

            {values.length ? (
              <div className="filter_multi_clear" onClick={() => onChange([])}>
                Сбросить выбор
              </div>
            ) : null}

            <div className="filter_multi_list">
              {!options.length ? <p className="filter_multi_empty">{emptyText}</p> : null}
              {options.length > 0 && !visibleOptions.length ? (
                <p className="filter_multi_empty">Ничего не найдено</p>
              ) : null}
              {visibleOptions.map((option) => (
                <label key={option.value} className="filter_multi_row">
                  <input
                    type="checkbox"
                    checked={values.includes(option.value)}
                    onChange={() => toggle(option.value)}
                  />
                  <span>{option.label}</span>
                </label>
              ))}
            </div>
          </div>
        ) : null}
      </div>
    </FilterField>
  );
}

/** Диапазон дат «с — по» (значения в формате YYYY-MM-DD). */
export function FilterDateRange({
  label, from, to, onChange, error, hint,
}) {
  return (
    <FilterField label={label} error={error} hint={hint}>
      <div className="filter_dates">
        <input
          type="date"
          className={classNames('filter_date', { filter_date_error: Boolean(error) })}
          value={from}
          max={to || undefined}
          onChange={(e) => onChange({ from: e.target.value, to })}
          aria-label={`${label}: с`}
        />
        <span className="filter_dates_dash">—</span>
        <input
          type="date"
          className={classNames('filter_date', { filter_date_error: Boolean(error) })}
          value={to}
          min={from || undefined}
          onChange={(e) => onChange({ from, to: e.target.value })}
          aria-label={`${label}: по`}
        />
      </div>
    </FilterField>
  );
}

/** Переключатель-галочка в стиле остальных фильтров. */
export function FilterCheckbox({ label, value, onChange }) {
  return (
    <div className="filter_field filter_field_checkbox">
      <MyCheckBox value={value} onClick={() => onChange(!value)} text={label} />
    </div>
  );
}

/**
 * Сворачиваемая панель фильтров.
 * chips: [{ key, label, reset }] — применённые фильтры; onRemoveChip(chip) снимает один.
 */
function FiltersPanel({
  chips = [], onRemoveChip, onReset, defaultOpen = false, children,
}) {
  const [isOpen, setIsOpen] = useState(defaultOpen);

  return (
    <div className="filters_panel">
      <div className="filters_panel_bar">
        <button
          type="button"
          className={classNames('filters_panel_toggle', { filters_panel_toggle_open: isOpen })}
          onClick={() => setIsOpen((prev) => !prev)}
        >
          Фильтры
          {chips.length ? <span className="filters_panel_badge">{chips.length}</span> : null}
          <span className="filters_panel_arrow" />
        </button>

        {chips.length ? (
          <div className="filters_panel_chips">
            {chips.map((chip) => (
              <span key={chip.key} className="filters_panel_chip">
                {chip.label}
                <button
                  type="button"
                  className="filters_panel_chip_remove"
                  onClick={() => onRemoveChip(chip)}
                  aria-label={`Снять фильтр: ${chip.label}`}
                >
                  ×
                </button>
              </span>
            ))}
            <button type="button" className="filters_panel_reset" onClick={onReset}>
              Сбросить все
            </button>
          </div>
        ) : (
          <span className="filters_panel_empty">Фильтры не заданы</span>
        )}
      </div>

      {isOpen ? <div className="filters_panel_body">{children}</div> : null}
    </div>
  );
}

export default FiltersPanel;
